import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService implements OnDestroy {
  private readonly timeoutMs = 10 * 60 * 1000; // 10 minutes

  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;

  private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
  private readonly activityHandler = () => this.resetInactivityTimer();
  private readonly visibilityHandler = () => this.handleVisibilityChange();
  private readonly focusHandler = () => this.onFocus();
  private readonly blurHandler = () => this.onBlur();

  constructor(private auth: AuthService, private router: Router) {}

  startWatching(): void {
    if (this.started) return;
    this.started = true;

    this.activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, this.activityHandler, { passive: true })
    );
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('focus', this.focusHandler);
    window.addEventListener('blur', this.blurHandler);

    this.resetInactivityTimer();
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }

  stopWatching(): void {
    if (!this.started) return;
    this.started = false;

    this.activityEvents.forEach((eventName) =>
      window.removeEventListener(eventName, this.activityHandler)
    );
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    window.removeEventListener('focus', this.focusHandler);
    window.removeEventListener('blur', this.blurHandler);

    this.clearInactivityTimer();
    this.clearHiddenTimer();
  }

  private resetInactivityTimer(): void {
    this.clearInactivityTimer();
    this.inactivityTimer = setTimeout(() => this.forceLogout('inactivity'), this.timeoutMs);
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.startHiddenTimer();
    } else {
      this.clearHiddenTimer();
      this.resetInactivityTimer();
    }
  }

  private onBlur(): void {
    this.startHiddenTimer();
  }

  private onFocus(): void {
    this.clearHiddenTimer();
    this.resetInactivityTimer();
  }

  private startHiddenTimer(): void {
    this.clearHiddenTimer();
    this.hiddenTimer = setTimeout(() => this.forceLogout('background'), this.timeoutMs);
  }

  private forceLogout(reason: 'inactivity' | 'background'): void {
    if (!this.auth.isAuthenticated()) return;

    console.warn(`[SessionTimeout] Logging out due to ${reason}.`);
    this.auth.logout();
    this.router.navigate(['/auth/login']);
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private clearHiddenTimer(): void {
    if (this.hiddenTimer) {
      clearTimeout(this.hiddenTimer);
      this.hiddenTimer = null;
    }
  }
}
